/**
 * The Billing tab's view model (BLU-1446), pure so it is tested without a DOM.
 *
 * ONE MONTH = ONE BILL, labelled by the period whose pieces it charges.
 *   - The current row is the NEXT invoice: Stripe makes it at the end of the
 *     current period (`nextInvoiceDate`), charging the pieces counted in that
 *     period plus the next fee in advance (handover L5). Label: the current
 *     period ("30 Sep to 30 Oct"), pill "So far".
 *   - A past row is one Stripe invoice, labelled by the period that CLOSED when
 *     it was made: the backend's `usagePeriod` (its metered line's period, or
 *     on a plan with no piece price the fee period of the invoice before it),
 *     so every row reads the same way as the current one, with our own piece
 *     count for the record. Periods are Stripe's own dates (the sign-up
 *     anniversary, L4), never calendar months.
 *   - The oldest invoice with no period of its own is the sign-up invoice
 *     (nothing closed yet): it is the rail's footer note ("Started ..."),
 *     not a month row. Only when the list is complete (the backend's
 *     `complete`, Stripe's `has_more === false`): a list cut at its cap may
 *     not reach the sign-up.
 *
 * Bill rows: a past month is Stripe's invoice lines one for one (they are the
 * truth). The current month is the backend's estimate as the three locked
 * rows. GST is never computed here: it is Stripe's, or a grey "added on the
 * invoice" line.
 */
import type { RouterOutputs } from '@/lib/trpc';
import { parseUnitCents } from './billing-format';

export type BillingOverview = RouterOutputs['billing']['getOverview'];
export type ActiveOverview = Extract<BillingOverview, { status: 'active' }>;
export type BillingMonth = RouterOutputs['billing']['listMonths']['months'][number];
export type BillLine = BillingMonth['lines'][number];
export type BilledPiecesPage = RouterOutputs['billing']['listPieces'];
export type BilledPiece = BilledPiecesPage['rows'][number];
export type BillingClient = NonNullable<BilledPiecesPage['clients']>[number];
export type BilledPiecesExport = RouterOutputs['billing']['exportPieces'];

export interface BillPeriod {
  start: string;
  end: string;
}

export type MonthStatus = 'so_far' | 'paid' | 'payment_failed' | 'due' | 'void' | 'not_paid';

export interface CurrentEntry {
  kind: 'current';
  /** `?month=` value. */
  token: string;
  period: BillPeriod;
  /** Ex GST. */
  amountCents: number;
  currency: string;
  pieces: number;
  status: 'so_far';
}

export interface InvoiceEntry {
  kind: 'invoice';
  token: string;
  /** The period whose pieces this invoice charges; null when none applies. */
  period: BillPeriod | null;
  month: BillingMonth;
  /** Ex GST. */
  amountCents: number;
  currency: string;
  /** Our own count for `period`, when the backend has one. */
  pieces: number | null;
  status: MonthStatus;
}

export type MonthEntry = CurrentEntry | InvoiceEntry;

export interface MonthRail {
  current: CurrentEntry;
  /** Newest first, the sign-up invoice excluded. */
  past: InvoiceEntry[];
  /** The sign-up invoice, shown as the rail's footer note. */
  first: InvoiceEntry | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** The UTC day of an instant, "2026-09-30": a bare token, the same for every viewer's time zone. */
export function periodToken(iso: string): string {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? new Date(ms).toISOString().slice(0, 10) : iso;
}

export function monthStatus(month: BillingMonth): MonthStatus {
  if (month.paymentFailed) return 'payment_failed';
  switch (month.status) {
    case 'paid':
      return 'paid';
    case 'open':
      return 'due';
    case 'void':
      return 'void';
    case 'uncollectible':
      return 'not_paid';
  }
}

/**
 * `complete`: the month list holds every invoice the account has (the
 * backend's `listMonths().complete`), so its oldest is the sign-up invoice.
 */
export function buildMonthRail(
  overview: ActiveOverview,
  months: readonly BillingMonth[] | undefined,
  complete = false,
): MonthRail {
  const current: CurrentEntry = {
    kind: 'current',
    token: periodToken(overview.currentPeriod.start),
    period: { start: overview.currentPeriod.start, end: overview.currentPeriod.end },
    amountCents: overview.estimate.totalCents,
    currency: overview.currency,
    pieces: overview.pieces.used,
    status: 'so_far',
  };
  if (!months || months.length === 0) return { current, past: [], first: null };

  const used = new Set<string>([current.token]);
  const entries = months.map((month): InvoiceEntry => {
    const period = month.usagePeriod;
    let token = period ? periodToken(period.start) : `inv-${month.number ?? month.id}`;
    // Two invoices for one period (a voided invoice and its re-issue): the
    // second gets the invoice id, so every row keeps a link of its own.
    if (used.has(token)) token = `inv-${month.id}`;
    used.add(token);
    return {
      kind: 'invoice',
      token,
      period,
      month,
      amountCents: month.subtotalCents,
      currency: month.currency,
      pieces: month.countedPieces,
      status: monthStatus(month),
    };
  });

  const oldest = entries.reduce<InvoiceEntry | null>(
    (found, entry) => (!found || Date.parse(entry.month.date) < Date.parse(found.month.date) ? entry : found),
    null,
  );
  const first = oldest && oldest.period === null && complete ? oldest : null;
  return { current, past: entries.filter((entry) => entry !== first), first };
}

export function findMonthEntry(rail: MonthRail, token: string | null): MonthEntry | null {
  if (!token || token === rail.current.token) return rail.current;
  return rail.past.find((entry) => entry.token === token) ?? (rail.first?.token === token ? rail.first : null);
}

// ─── Bill rows ───────────────────────────────────────────────────────────────

export interface BillRow {
  key: string;
  kind: BillLine['kind'];
  /** Stripe's description: the title of an `other` line. */
  description: string | null;
  period: BillPeriod | null;
  qty: number | null;
  unitCents: number | null;
  amountCents: number;
  /** A deliberate A$0.00 (included pieces): shown as money, not a "-". */
  freeByDesign: boolean;
}

export interface BillTotals {
  exGstCents: number;
  /** Stripe's GST and total inc GST, when Stripe charged or previewed GST. */
  gst: { gstCents: number; totalCents: number } | null;
  /** No GST figure to show yet, but the invoice will add it ("GST is added on the invoice."). */
  gstOnInvoice: boolean;
}

export interface BillModel {
  rows: BillRow[];
  totals: BillTotals;
}

export interface PieceMeter {
  used: number;
  included: number;
  /** Pieces inside the allowance (min of used and included). */
  includedUsed: number;
  extra: number;
  /** 0..100. */
  percent: number;
  over: boolean;
}

/** The allowance meter, or null on a plan with no piece price (no allowance applies). */
export function pieceMeter(overview: ActiveOverview): PieceMeter | null {
  const { used, included, extra } = overview.pieces;
  if (!overview.hasPiecePrice || included === null || extra === null) return null;
  const includedUsed = Math.min(used, included);
  const percent = included > 0 ? Math.min(100, (used / included) * 100) : 100;
  return { used, included, includedUsed, extra, percent, over: extra > 0 };
}

export function currentBill(overview: ActiveOverview): BillModel {
  const { estimate, currentPeriod } = overview;
  const usage: BillPeriod = { start: currentPeriod.start, end: currentPeriod.end };
  const rows: BillRow[] = [
    {
      key: 'fee',
      kind: 'fee',
      description: null,
      period: { start: currentPeriod.feePeriodStart, end: currentPeriod.feePeriodEnd },
      qty: 1,
      unitCents: estimate.feeCents,
      amountCents: estimate.feeCents,
      freeByDesign: false,
    },
  ];
  const meter = pieceMeter(overview);
  if (meter && estimate.extraCents !== null) {
    rows.push(
      {
        key: 'included',
        kind: 'included_pieces',
        description: null,
        period: usage,
        qty: meter.includedUsed,
        unitCents: 0,
        amountCents: 0,
        freeByDesign: true,
      },
      {
        key: 'extra',
        kind: 'extra_pieces',
        description: null,
        period: usage,
        qty: meter.extra,
        unitCents: parseUnitCents(overview.plan.extraPieceUnitAmountDecimal),
        amountCents: estimate.extraCents,
        freeByDesign: false,
      },
    );
  }

  // Stripe's preview can lag our meter (handover §10). Its GST is only shown
  // when it was worked out on the same subtotal as the bill on screen, so the
  // three footer rows always add up; otherwise the invoice adds it.
  const preview = overview.stripePreview;
  const sameSubtotal = preview !== null && preview.subtotalCents === estimate.totalCents;
  return {
    rows,
    totals: {
      exGstCents: estimate.totalCents,
      gst: sameSubtotal && preview.gstCents > 0 ? { gstCents: preview.gstCents, totalCents: preview.totalCents } : null,
      gstOnInvoice: !sameSubtotal,
    },
  };
}

export function pastBill(month: BillingMonth): BillModel {
  const rows = month.lines.map(
    (line, index): BillRow => ({
      key: `${index}`,
      kind: line.kind,
      description: line.description,
      period: line.period,
      qty: line.quantity,
      unitCents: parseUnitCents(line.unitAmountDecimal),
      amountCents: line.amountCents,
      freeByDesign: line.kind === 'included_pieces',
    }),
  );
  return {
    rows,
    totals: {
      exGstCents: month.subtotalCents,
      gst: month.taxCents > 0 ? { gstCents: month.taxCents, totalCents: month.totalCents } : null,
      gstOnInvoice: false,
    },
  };
}

/** A period worth drawing as a date range (a one-off line's period is one instant). */
export function isRealPeriod(period: BillPeriod | null): period is BillPeriod {
  return period !== null && Date.parse(period.end) - Date.parse(period.start) >= DAY_MS;
}
